import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import './AuthForms.css';

interface SignInFormProps {
  onSuccess?: () => void;
  onSwitchToSignUp?: () => void;
  onForgotPassword?: () => void;
}

export const SignInForm: React.FC<SignInFormProps> = ({ 
  onSuccess, 
  onSwitchToSignUp, 
  onForgotPassword 
}) => {
  const { signIn } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);

  // Check if user is coming from email verification
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const verified = urlParams.get('verified') === 'true';
    const success = urlParams.get('success') === 'confirmed';
    
    if (verified || success) {
      setIsVerifiedUser(true);
    }
  }, [location.search]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Please enter your password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await signIn(formData.email, formData.password);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      
      // Handle specific Cognito errors with user-friendly messages
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.code === 'NotAuthorizedException') {
        errorMessage = 'Email or password is incorrect. Please check and try again.';
      } else if (error.code === 'UserNotFoundException') {
        errorMessage = 'No account found with this email address. Please check your email or create a new account.';
      } else if (error.code === 'UserNotConfirmedException') {
        errorMessage = 'Please check your email and click the verification link before signing in.';
      } else if (error.code === 'PasswordResetRequiredException') {
        errorMessage = 'You need to reset your password. Please use the "Forgot Password" option below.';
      } else if (error.code === 'TooManyRequestsException') {
        errorMessage = 'Too many sign-in attempts. Please wait a few minutes and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (typeof value === 'string' && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="auth-form-container">
      <div className="auth-form">
        <h1 className="auth-title">
          {isVerifiedUser ? 'Email Verified Successfully!' : 'Welcome Back!'}
        </h1>
        <p className="auth-subtitle">
          {isVerifiedUser 
            ? 'Great! Your email has been verified. Now you can sign in to start using My Nerdy Son.'
            : 'Sign in to continue getting friendly help with your technology questions'
          }
        </p>

        {isVerifiedUser && (
          <div className="success-message verification-success" role="alert">
            ✅ Your email verification is complete! Please enter your email and password below to access your account.
          </div>
        )}

        {errors.general && (
          <div className="error-message general-error" role="alert">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Email Field */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter your email address"
              required
              autoComplete="email"
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <div id="email-error" className="error-message" role="alert">
                {errors.email}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.password && (
              <div id="password-error" className="error-message" role="alert">
                {errors.password}
              </div>
            )}
          </div>

          {/* Remember Me Checkbox */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="checkbox-input"
                checked={formData.rememberMe}
                onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
              />
              <span className="checkbox-text">Keep me signed in</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        {/* Forgot Password Link */}
        <div className="auth-links">
          <button
            type="button"
            className="link-button"
            onClick={onForgotPassword}
          >
            Forgot your password?
          </button>
        </div>

        {/* Switch to Sign Up */}
        <div className="auth-switch">
          <p>Don't have an account yet?</p>
          <button
            type="button"
            className="auth-button secondary"
            onClick={onSwitchToSignUp}
          >
            Create Account
          </button>
        </div>

        {/* Help Text */}
        <div className="auth-help">
          <p>
            <strong>Need help?</strong> If you're having trouble signing in, 
            you can use the "Forgot your password?" link above to reset your password.
          </p>
        </div>
      </div>
    </div>
  );
};